import { useCallback, useEffect, useState } from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";

import { useAccess } from "hooks/useAccess";
import { ROLES } from "helpers/permissions";
import { LojaPageContainer } from "layouts/pages/loja/common";
import {
  getDashboardEstatisticas,
  getOrcamentoEstatisticasResumo,
  type DashboardEstatisticasResponse,
  type OrcamentoEstatisticasResumo,
  type PeriodoEstatisticasPreset,
} from "services/estatisticasDashboard";

import EstatisticasPeriodoFilter from "./components/EstatisticasPeriodoFilter";
import {
  ChartSkeleton,
  ListSkeleton,
} from "./components/EstatisticasSkeleton";
import {
  SecaoEventos,
  SecaoFinanceiro,
  SecaoLoja,
  SecaoOrcamentos,
} from "./components/EstatisticasSections";

type TabKey = "eventos" | "financeiro" | "loja" | "orcamentos";

function Analytics(): JSX.Element {
  const { isSuperAdmin, hasRole } = useAccess();
  const showFinance =
    isSuperAdmin ||
    hasRole(ROLES.ROLE_USER_FIN) ||
    hasRole(ROLES.ROLE_ADMIN) ||
    hasRole(ROLES.ROLE_MASTER);

  const cardLg: 3 | 4 = showFinance ? 3 : 4;

  const [periodo, setPeriodo] = useState<PeriodoEstatisticasPreset>("ytd");
  const [tab, setTab] = useState<TabKey>("eventos");
  const [data, setData] = useState<DashboardEstatisticasResponse | null>(null);
  const [orcamento, setOrcamento] = useState<OrcamentoEstatisticasResumo | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboard, orcamentoRes] = await Promise.allSettled([
        getDashboardEstatisticas(periodo),
        getOrcamentoEstatisticasResumo(),
      ]);

      if (dashboard.status === "fulfilled") {
        setData(dashboard.value);
      } else {
        setData(null);
        setError("Não foi possível carregar as estatísticas.");
      }

      if (orcamentoRes.status === "fulfilled") {
        setOrcamento(orcamentoRes.value);
      } else {
        setOrcamento(null);
      }
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sectionProps = {
    data: data!,
    orcamento,
    showFinance,
    cardLg,
  };

  const periodoSubtitle = data?.periodo.periodoLabel
    ? `Período: ${data.periodo.periodoLabel}`
    : loading
      ? "Carregando período…"
      : undefined;

  return (
    <LojaPageContainer
      title="Estatísticas gerais"
      subtitle={periodoSubtitle}
      actions={
        <EstatisticasPeriodoFilter value={periodo} onChange={setPeriodo} />
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MDBox
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          pt: 0.5,
          pb: 2,
          mb: 1,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Eventos" value="eventos" />
          {showFinance && <Tab label="Financeiro" value="financeiro" />}
          {showFinance && <Tab label="Loja" value="loja" />}
          <Tab label="Orçamentos" value="orcamentos" />
        </Tabs>
      </MDBox>

      <MDBox pt={2}>
      {loading && !data ? (
        <Grid container spacing={3}>
          <ChartSkeleton height={400} />
          <ListSkeleton />
        </Grid>
      ) : data ? (
        <>
          {tab === "eventos" && <SecaoEventos {...sectionProps} />}
          {tab === "financeiro" && showFinance && (
            <SecaoFinanceiro {...sectionProps} />
          )}
          {tab === "loja" && showFinance && <SecaoLoja {...sectionProps} />}
          {tab === "orcamentos" && (
            <SecaoOrcamentos orcamento={orcamento} />
          )}
        </>
      ) : null}
      </MDBox>
    </LojaPageContainer>
  );
}

export default Analytics;
